import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { actionHubApi, paymentsApi, tasksApi, huddleApi, growthApi } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Crown, Rocket, DollarSign, Target, Zap, CheckCircle2, PenLine, ChevronRight, Trophy, TrendingUp, ChevronDown, BookOpen, X, Loader2, Flame, Megaphone } from "lucide-react";
import { fmtUSDFull } from "@/lib/format";
import SignatureCapture from "./SignatureCapture";

const KIND_ICON = { draft: PenLine, insight: Zap, task: CheckCircle2 };
const KIND_COLOR = { draft: "#F59E0B", insight: "#8B5CF6", task: "#10B981" };
const KIND_LABEL = { draft: "DRAFT", insight: "INSIGHT", task: "TASK" };

export default function ActionHub() {
  const [data, setData] = useState({ items: [], total_projected_revenue: 0, counts: {} });
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(null);
  const [pms, setPms] = useState([]);
  const [signOpen, setSignOpen] = useState(null); // {kind, id, item}
  const [huddles, setHuddles] = useState([]);
  const [signingOff, setSigningOff] = useState(null);
  const [riskLog, setRiskLog] = useState(null);
  const [pulse, setPulse] = useState(null);
  const [maintaining, setMaintaining] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, m, h, r, p] = await Promise.all([
        actionHubApi.list(),
        paymentsApi.listMethods().catch(() => []),
        huddleApi.pending().catch(() => []),
        huddleApi.overrides().catch(() => null),
        growthApi.channelPulse().catch(() => null),
      ]);
      setData(d);
      setPms(m);
      setHuddles(h);
      setRiskLog(r);
      setPulse(p);
    } catch (e) { console.error("action hub load failed", e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const refreshPresence = async () => {
    setMaintaining(true);
    try {
      const r = await growthApi.brandMaintenance();
      if (r.staged.length) toast.success(`Brand momentum — ${r.staged.length} presence draft(s) staged: ${r.staged.map((s) => `${s.venture}/${s.platform}`).join(", ")}`);
      else toast.info(`Channel pulse checked (${r.channels_checked} channels, ${r.silent} silent) — nothing new to stage`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Brand maintenance failed");
    } finally { setMaintaining(false); }
  };

  const signOff = async (h) => {
    setSigningOff(h.id);
    try {
      const r = await huddleApi.execute(h.id);
      toast.success(`SIGNED & EXECUTED — ${r.detail}`);
      if (r.dissent_override_logged) {
        toast.warning("Dissent override logged — your risk appetite was recorded for boardroom calibration");
      }
      toast.info("Proof of work archived to Executed Vault");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-off failed");
    } finally { setSigningOff(null); }
  };

  const executeItem = async (item, opts = {}) => {
    setExecuting(item.id);
    try {
      const r = await actionHubApi.execute(item._action_kind, item.id, opts);
      toast.success(`Executed · ${item.title || item.body?.slice(0, 40) || "action"}`);
      if (r.vault_doc_id) toast.info("Archived to Executed Vault");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Execute failed";
      toast.error(detail);
    } finally { setExecuting(null); setSignOpen(null); }
  };

  const onClickExecute = (item) => {
    // Financial insights need a payment method + sig; drafts optionally take a sig.
    const isFinancial = item._action_kind === "insight" && Number(item.estimated_cost || 0) > 0;
    if (isFinancial) {
      if (!pms.length) { toast.error("Add a payment method in Command Wallet first"); return; }
      // Show sig capture with wallet picker inside
      setSignOpen({ kind: "insight", id: item.id, item, financial: true });
    } else if (item._action_kind === "draft") {
      setSignOpen({ kind: "draft", id: item.id, item, financial: false });
    } else {
      executeItem(item);
    }
  };

  return (
    <section className="uc-card p-6 uc-fade" data-testid="action-hub">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="uc-chip mb-2"><Crown size={11}/> STRATEGIC ACTION HUB · REVENUE-FIRST</div>
          <h2 className="font-serif-display text-2xl">What makes you money — right now.</h2>
          <p className="text-white/50 text-xs mt-1 max-w-xl">
            Every draft, insight, and task the agents have staged, sorted by projected revenue.
            One tap approves, sends, and archives to the Executed Vault.
          </p>
        </div>
        <div className="text-right">
          <div className="font-mono-uc text-[10px] uppercase tracking-widest text-white/50">Projected Revenue Queued</div>
          <div className="font-serif-display text-3xl text-emerald-400" data-testid="hub-total-projected">
            {fmtUSDFull(data.total_projected_revenue)}
          </div>
          <div className="flex gap-3 justify-end mt-1 font-mono-uc text-[10px] text-white/40">
            <span>{data.counts?.drafts || 0} drafts</span>
            <span>{data.counts?.insights || 0} insights</span>
            <span>{data.counts?.tasks || 0} tasks</span>
          </div>
        </div>
      </div>

      {loading && <div className="text-white/40 text-sm">Loading action hub…</div>}
      {!loading && data.items.length === 0 && (
        <div className="text-white/40 text-[11px] italic py-6 border border-dashed border-white/10 text-center">
          The Board is clear. Run an agent to stage new revenue actions.
        </div>
      )}

      <div className="space-y-1.5 max-h-[600px] overflow-y-auto uc-scroll pr-1" data-testid="action-hub-list">
        {/* Brand Momentum — silent channels flagged by the Creator's 72h pulse */}
        {pulse && pulse.silent_count > 0 && (
          <div className="border border-amber-500/25 bg-amber-500/[0.03] px-2.5 py-1.5 flex items-center gap-2 flex-wrap" data-testid="brand-momentum-strip">
            <span className="font-mono-uc text-[9px] uppercase tracking-widest text-amber-400 flex items-center gap-1">
              <Megaphone size={9}/> Brand Momentum · {pulse.silent_count} channel{pulse.silent_count === 1 ? "" : "s"} silent &gt;{pulse.silence_threshold_hours}h
            </span>
            <span className="font-mono-uc text-[9px] text-white/45 truncate" data-testid="brand-momentum-detail">
              {pulse.ventures.map((v) => {
                const silent = v.channels.filter((c) => c.silent);
                return silent.length ? `${v.venture_name}: ${silent.map((c) => c.platform).join("/")}` : null;
              }).filter(Boolean).join(" · ")}
            </span>
            <button
              data-testid="brand-momentum-refresh-btn"
              onClick={refreshPresence}
              disabled={maintaining}
              className="ml-auto font-mono-uc text-[8px] uppercase tracking-wider px-1.5 py-0.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 disabled:opacity-50 flex items-center gap-1 flex-none"
            >
              {maintaining ? <Loader2 size={8} className="animate-spin"/> : <Megaphone size={8}/>} Refresh Presence
            </button>
          </div>
        )}
        {/* Dissent Override Log — the Commander's recorded risk appetite */}
        {riskLog && riskLog.total > 0 && (
          <div className="border border-red-500/25 bg-red-500/[0.03] px-2.5 py-1.5 flex items-center gap-2 flex-wrap" data-testid="dissent-override-log">
            <span className="font-mono-uc text-[9px] uppercase tracking-widest text-red-400 flex items-center gap-1">
              <Flame size={9}/> Risk Appetite · {riskLog.total} dissent override{riskLog.total === 1 ? "" : "s"}
            </span>
            <span className="font-mono-uc text-[9px] text-white/45" data-testid="override-agent-tally">
              overridden: {Object.entries(riskLog.overridden_by_agent || {}).map(([a, c]) => `${a}×${c}`).join(" · ")}
            </span>
            <span className="ml-auto font-mono-uc text-[8px] uppercase tracking-wider text-white/30">agents calibrate to this</span>
          </div>
        )}
        {/* Executive Sign-Off — huddle briefings awaiting the Commander's signature */}
        {huddles.map((h) => (
          <div key={h.id} className="border border-amber-500/40 bg-amber-500/[0.04] p-3" data-testid={`huddle-signoff-${h.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <Crown size={13} className="text-amber-400 flex-none"/>
              <span className="font-mono-uc text-[9px] uppercase tracking-widest text-amber-400">Executive Sign-Off · Boardroom Huddle</span>
              {h.convened_by === "auto-sweep" && (
                <span className="font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-teal-500/40 text-teal-300" data-testid={`huddle-auto-badge-${h.id}`}>
                  Auto-Huddle · Top Pick
                </span>
              )}
              {h.convened_by === "lead-console" && (
                <span className="font-mono-uc text-[8px] uppercase tracking-widest px-1.5 py-0.5 border border-emerald-500/40 text-emerald-300" data-testid={`huddle-lead-badge-${h.id}`}>
                  Boardroom Approved · Complete Package
                </span>
              )}
              <span
                data-testid={`huddle-impact-${h.id}`}
                className={`ml-auto font-mono-uc text-[10px] px-1.5 py-0.5 border flex-none ${h.impact?.usd >= 0 ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`}
                title={h.impact?.rationale}
              >
                Impact {h.impact?.usd >= 0 ? "+" : ""}{fmtUSDFull(h.impact?.usd || 0)} · {h.impact?.confidence}
              </span>
            </div>
            <div className="text-[13px] font-medium text-white/90 mt-1">{h.title}</div>
            <p className="text-[11px] text-white/60 mt-1 leading-relaxed">{h.briefing}</p>
            {(h.dissents || []).map((d, i) => (
              <div key={i} className="mt-1.5 border border-red-500/40 bg-red-500/[0.06] px-2 py-1.5" data-testid={`huddle-dissent-${h.id}-${i}`}>
                <span className="font-mono-uc text-[9px] uppercase tracking-widest text-red-400">⚡ Dissenting Opinion · {d.agent}</span>
                <p className="text-[11px] text-red-200/80 mt-0.5">{d.reason}</p>
              </div>
            ))}
            <details className="mt-1.5">
              <summary className="font-mono-uc text-[9px] uppercase tracking-widest text-white/40 cursor-pointer hover:text-white/70">
                Ready-to-Sign artifact · {h.artifact?.title || "attached"}
              </summary>
              <pre className="whitespace-pre-wrap text-[11px] text-white/70 bg-black border border-white/10 p-2 mt-1 max-h-48 overflow-y-auto uc-scroll font-sans">{h.artifact?.body}</pre>
            </details>
            <div className="flex items-center justify-between mt-2">
              <span className="font-mono-uc text-[9px] text-white/35">
                {h.reviews?.length || 7} agents · {(h.dissents || []).length} dissent(s) · via {h.artifact?.delivery?.method || "file"}
              </span>
              <Button
                data-testid={`huddle-execute-${h.id}`}
                onClick={() => signOff(h)}
                disabled={signingOff === h.id}
                className="uc-btn-amber rounded-none h-8 px-3 text-[10px] font-mono-uc uppercase tracking-wider"
              >
                {signingOff === h.id ? <Loader2 size={11} className="mr-1 animate-spin"/> : <Rocket size={11} className="mr-1"/>}
                Approve & Execute
              </Button>
            </div>
          </div>
        ))}
        {data.items.map((it) => (
          <ActionRow key={it.id} item={it}
                     executing={executing === it.id}
                     onExecute={() => onClickExecute(it)}/>
        ))}
      </div>

      {signOpen && (
        <SignatureCapture
          open={!!signOpen}
          onClose={() => setSignOpen(null)}
          onConfirm={(sig, pmId) => executeItem(signOpen.item, {
            signature_data_url: sig?.dataUrl,
            signature_name: sig?.name,
            payment_method_id: pmId,
          })}
          title={signOpen.item?.title || "Action"}
          financial={signOpen.financial}
          paymentMethods={pms}
        />
      )}
    </section>
  );
}

function ActionRow({ item, executing, onExecute }) {
  const Icon = KIND_ICON[item._action_kind] || Zap;
  const color = KIND_COLOR[item._action_kind] || "#71717A";
  const kindLabel = KIND_LABEL[item._action_kind] || "ACTION";
  const [open, setOpen] = useState(false);
  const [artifact, setArtifact] = useState(null);
  const [reading, setReading] = useState(false);
  const revenue = Number(item.projected_revenue || 0);
  const cost = Number(item.estimated_cost || 0);
  const impact = item.goal_impact;
  const isFinancial = item._action_kind === "insight" && cost > 0;
  const hasArtifact = item._action_kind === "task" && item.artifact_ref?.kind === "vault" && item.artifact_ref?.ref;

  const readArtifact = async () => {
    setReading(true);
    try {
      setArtifact(await tasksApi.readArtifact(item.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not unseal artifact");
    } finally { setReading(false); }
  };

  return (
    <div className="border border-white/10 hover:border-white/20 transition-colors" data-testid={`action-${item._action_kind}-${item.id}`}>
      <div className="px-2.5 py-2 flex items-start gap-2.5">
        <Icon size={14} className="mt-0.5 flex-none" style={{ color }}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="font-mono-uc text-[9px] uppercase tracking-widest" style={{ color }}>
              {kindLabel}
            </span>
            {item.venture_name && (
              <span data-testid={`action-venture-${item.id}`} className="font-mono-uc text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-teal-500/40 text-teal-300">
                {item.venture_name}
              </span>
            )}
            {item.source === "brand_maintenance" && (
              <span className="font-mono-uc text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/40 text-amber-300" data-testid={`action-brand-badge-${item.id}`}>
                Brand Momentum
              </span>
            )}
            {item.source === "psych_outreach" && (
              <span className="font-mono-uc text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-purple-500/40 text-purple-300" data-testid={`action-psych-badge-${item.id}`}>
                Psych Outreach
              </span>
            )}
            {item.source === "nexus_intel" && (
              <span className="font-mono-uc text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-cyan-500/40 text-cyan-300" data-testid={`action-intel-badge-${item.id}`}>
                {item.agent === "scout" ? "Trigger Lead" : "Global Threat"}
              </span>
            )}
            {item.source === "nexus_bridge" && (
              <span className="font-mono-uc text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-cyan-500/40 text-cyan-300" data-testid={`action-bridge-badge-${item.id}`}>
                Nexus Bridge
              </span>
            )}
            {item.execution_payload?.kind && (
              <span className="font-mono-uc text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-emerald-500/40 text-emerald-300" data-testid={`action-exec-badge-${item.id}`}>
                {item.execution_payload.kind === "python" ? "RUNS CODE" : "RUNS BROWSER"}
              </span>
            )}
            {hasArtifact && (
              <button
                data-testid={`action-artifact-btn-${item.id}`}
                onClick={readArtifact}
                disabled={reading}
                className="font-mono-uc text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 flex items-center gap-1 transition-colors disabled:opacity-50"
              >
                {reading ? <Loader2 size={9} className="animate-spin"/> : <BookOpen size={9}/>} Sealed Artifact
              </button>
            )}
            {revenue > 0 && (
              <span className="font-mono-uc text-[10px] text-emerald-400 flex items-center gap-1" data-testid={`action-revenue-${item.id}`}>
                <TrendingUp size={10}/> +{fmtUSDFull(revenue)}
              </span>
            )}
            {isFinancial && (
              <span className="font-mono-uc text-[10px] text-red-300 flex items-center gap-1">
                <DollarSign size={10}/> -{fmtUSDFull(cost)}
              </span>
            )}
          </div>
          <div className="text-[13px] font-medium text-white/90 mt-0.5 leading-snug truncate" title={item.title}>{item.title || item.body?.slice(0, 80) || "Untitled action"}</div>
          {impact && (
            <div className="flex items-center gap-1 text-[9px] text-amber-300/80 mt-0.5 font-mono-uc truncate" data-testid={`action-impact-${item.id}`}>
              <Target size={8} className="flex-none"/> <span className="truncate">{impact}</span>
            </div>
          )}
          {open && (item.body || item.message) && (
            <div className="mt-2 p-2 bg-black/40 border border-white/5 text-[11px] text-white/70 whitespace-pre-wrap max-h-40 overflow-y-auto">
              {(item.body || item.message).slice(0, 900)}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-none">
          {(item.body || item.message) && (
            <button
              onClick={() => setOpen((v) => !v)}
              className="font-mono-uc text-[9px] uppercase tracking-widest text-white/40 hover:text-white/70 flex items-center gap-1"
            >
              {open ? "hide" : "preview"} <ChevronDown size={9} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
            </button>
          )}
          <Button
            data-testid={`action-execute-${item._action_kind}-${item.id}`}
            onClick={onExecute}
            disabled={executing}
            className="uc-btn-amber rounded-none h-7 px-2.5 text-[10px] font-mono-uc uppercase tracking-wider"
          >
            {executing ? "…" : (<><Rocket size={10} className="mr-1"/> Execute</>)}
          </Button>
        </div>
      </div>
      {artifact && <ArtifactReaderModal artifact={artifact} onClose={() => setArtifact(null)} />}
    </div>
  );
}

function ArtifactReaderModal({ artifact, onClose }) {
  // Portal to <body> — same stacking-context trap as the wallet modal.
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/75 flex items-center justify-center p-4" onClick={onClose} data-testid="artifact-reader-modal">
      <div className="bg-[#0A0A0A] border border-amber-500/30 max-w-2xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-white/10 flex items-start justify-between gap-3 bg-amber-500/5">
          <div className="min-w-0">
            <div className="font-mono-uc text-[9px] uppercase tracking-[0.25em] text-amber-400/80 mb-1">
              THE SOVEREIGN UMBRELLA COMMAND · VAULT ARTIFACT · {(artifact.category || "sealed").toUpperCase()}
            </div>
            <div className="font-serif-display text-xl leading-tight truncate">{artifact.title}</div>
            <div className="font-mono-uc text-[10px] text-white/40 mt-1 truncate">
              {artifact.source_id || ""} {artifact.created_at ? `· sealed ${artifact.created_at.slice(0, 10)}` : ""}
              {artifact.signature?.name ? ` · signed: ${artifact.signature.name}` : ""}
            </div>
          </div>
          <button data-testid="artifact-reader-close" onClick={onClose} className="text-white/40 hover:text-white transition-colors flex-none"><X size={16}/></button>
        </div>
        <pre className="flex-1 overflow-y-auto uc-scroll p-5 font-mono-uc text-xs text-white/75 whitespace-pre-wrap leading-relaxed" data-testid="artifact-reader-content">
          {artifact.content}
        </pre>
      </div>
    </div>,
    document.body
  );
}
